import React, {createContext, useEffect, useReducer, useContext} from 'react';
import {useLocation, Navigate} from 'react-router-dom';
import {
  login,
  getMyVenue,
  resendVenueCode,
  getMyRooms,
  getMyDevices,
  updateVenueAndRooms,
} from '../../api';
import LoginManager from '../../libs/LoginManager';
import LoggedoutPage from '../../components/pages/LoggedoutPage';

const loginManager = LoginManager.getSharedInstance();
const UserContext = createContext();

const initialState = {
  venue: null,
  rooms: [],
  devices: [],
};

function reducer(state, action) {
  console.log(state, action);
  switch (action.type) {
    case 'signin': {
      return {
        ...state,
        venue: action.venue,
        rooms: action.rooms,
        devices: action.devices,
      };
    }
    case 'signout': {
      return {
        ...state,
        venue: null,
        rooms: [],
        devices: [],
      };
    }
    case 'set_rooms': {
      return {
        ...state,
        rooms: action.rooms,
      };
    }
    case 'set_devices': {
      return {
        ...state,
        devices: action.devices,
      };
    }
    default:
      throw new Error(`${action.type} is not defined.`);
  }
}

export const UserContextProvider = ({api, children}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  //const { pathname, key } = useLocation();

  const fetchMyData = async () => {
    let [venue, rooms, devices] = await Promise.all([
      getMyVenue(),
      getMyRooms(),
      getMyDevices(),
    ]);
    dispatch({
      type: 'signin',
      venue,
      rooms,
      devices,
    });
  };

  useEffect(() => {
    if (loginManager.isLoggedIn()) {
      (async () => {
        window.incrementLoading();
        try {
          await fetchMyData();
        } catch (e) {
          console.error(e);
        }
        window.decrementLoading();
      })();
    }

    const handler = () => {
      dispatch({
        type: 'signout',
      });
    };
    loginManager.addEventListener('invalid_token', handler);
    return () => {
      loginManager.removeEventListener('invalid_token', handler);
    };
  }, []);

  let signin = async (venue_code, email, password, keep, callback) => {
    try {
      window.incrementLoading();
      let result = await login({venue_code, email, password});
      loginManager.setKeepLogin(!!keep);
      loginManager.setAccessToken(result.expires_in, result.access_token);
      await fetchMyData();
      window.clearLoading();
      callback();
    } catch (e) {
      window.clearLoading();
      callback(e);
    }
  };

  let signout = (callback) => {
    loginManager.removeAccessToken();
    dispatch({
      type: 'signout',
    });
    callback && callback();
  };

  let isLoggedIn = () => {
    return loginManager.isLoggedIn();
  };

  let requestVenueCode = async (email, callback) => {
    try {
      window.incrementLoading();
      await resendVenueCode({email});
      window.clearLoading();
      callback();
    } catch (e) {
      window.clearLoading();
      callback(e);
    }
  };

  let doUpdateVenueAndRooms = async (venue, rooms) => {
    try {
      window.incrementLoading();
      await updateVenueAndRooms({venue, rooms});
      window.clearLoading();
    } catch (e) {
      window.clearLoading();
      throw e;
    }
  };

  const value = {
    state,
    dispatch,
    signin,
    signout,
    isLoggedIn,
    requestVenueCode,
    doUpdateVenueAndRooms,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useAuth = () => {
  return useContext(UserContext);
};

export const RequireAuth = ({children}) => {
  let auth = useAuth();
  let location = useLocation();
  window.initialFetching = false;
  if (window.logged_out_now) {
    return <LoggedoutPage/>;
  }

  if (!auth.isLoggedIn()) {
    return (
        <Navigate to={`/login${location.search}`} state={{from: location}}/>
    );
  }

  window.initialFetching = true;
  if (!auth.state.venue || !auth.state.rooms.length) {
    return '';
  }
  window.initialFetching = false;

  return children;
};

export const WeakAuth = ({children}) => {
  let auth = useAuth();
  if (window.logged_out_now) {
    return <LoggedoutPage/>;
  }
  return children;
};

export default UserContext;
